import { ArrowRight, Target } from 'lucide-react';
import { Button } from '@ai-tutor/ui';
import type { CitedByte } from '@/lib/api-client';

// The lead paragraph, heading scale and list spacing. Arbitrary variants are
// used where a `prose-*` modifier cannot express the relationship (first
// child, h2 followed by h3, a paragraph followed by a list).
export const LESSON_PROSE_TYPE = [
  'prose-headings:font-semibold prose-headings:tracking-display',
  '[&>p:first-child]:text-lg [&>p:first-child]:leading-relaxed',
  'prose-h1:text-2xl',
  'prose-h2:mb-4 prose-h2:mt-12 prose-h2:border-t prose-h2:border-foreground/10 prose-h2:pt-8 prose-h2:text-[1.375rem] prose-h2:leading-snug',
  '[&>h2:first-child]:mt-0 [&>h2:first-child]:border-0 [&>h2:first-child]:pt-0',
  'prose-h3:mb-2 prose-h3:mt-8 prose-h3:text-[1.0625rem]',
  '[&_h2+h3]:mt-5',
  '[&_p:has(+ol)]:mb-2 [&_p:has(+ul)]:mb-2 [&_p+ol]:mt-2 [&_p+ul]:mt-2',
].join(' ');

export const LESSON_PROSE_CODE =
  'prose-code:rounded-sm prose-code:bg-muted prose-code:px-1.5 prose-code:py-0.5 prose-code:font-code prose-code:text-foreground prose-pre:bg-muted prose-pre:text-foreground dark:prose-code:bg-neutral-800 dark:prose-code:text-neutral-100 dark:prose-pre:bg-neutral-800 dark:prose-pre:text-neutral-100';

/** Average adult silent-reading pace for study material, words per minute. */
const WORDS_PER_MINUTE = 200;

interface TextLessonHeaderProps {
  title: string;
  wordCount?: number;
  citedBytes: CitedByte[];
}

/**
 * The entry point above the lesson prose: the title plus one mono metadata
 * line. Without it the Text tab opened straight onto a paragraph with nothing
 * to say what the reader was about to read or how long it would take.
 *
 * The source count is the number of DISTINCT sources the prose cites, not the
 * number of citations, so it is omitted when the lesson cites nothing.
 *
 * Rules inside the reading column are `foreground/10`, not `--border`: they
 * divide sections of one document rather than edge a card, so they sit as
 * light as the marketing site's hairline. The metadata grey is
 * `muted-foreground/90`, the lightest step that clears 4.5:1 on both the
 * lesson panel (5.1) and the bare page ground (4.74); `/85` failed axe on the
 * ground at 4.24. The marketing site's lighter grey is 2.9:1.
 */
export function TextLessonHeader({
  title,
  wordCount,
  citedBytes,
}: TextLessonHeaderProps) {
  const minutes = wordCount
    ? Math.max(1, Math.round(wordCount / WORDS_PER_MINUTE))
    : null;
  const sourceCount = new Set(citedBytes.map(c => c.source_id)).size;
  const meta = [
    minutes && `${minutes} min read`,
    sourceCount > 0 &&
      `${sourceCount} ${sourceCount === 1 ? 'source' : 'sources'}`,
  ];

  return <LessonPanelHeader title={title} meta={meta} />;
}

interface LessonPanelHeaderProps {
  title: string;
  /** Short facts joined into one mono line, e.g. `['4 min read', '2 sources']`. */
  meta: (string | number | false | null | undefined)[];
  className?: string;
}

/** Title plus mono metadata line, shared by the Text and Podcast tabs so every
 *  lesson format opens the same way. A plain `div`, not `<header>`/`<aside>`
 *  (here and in the end card): both sit inside the lesson's scroll `region`,
 *  and a nested banner/complementary landmark fails axe. */
export function LessonPanelHeader({
  title,
  meta,
  className = 'mb-8',
}: LessonPanelHeaderProps) {
  const facts = meta.filter(Boolean);
  return (
    <div className={`border-b border-foreground/10 pb-6 ${className}`}>
      <h1 className='text-2xl font-semibold leading-tight tracking-display text-foreground sm:text-[1.75rem]'>
        {title}
      </h1>
      {facts.length > 0 && (
        <p className='mt-3 font-mono text-label font-normal text-muted-foreground/90'>
          {facts.join(' · ')}
        </p>
      )}
    </div>
  );
}

interface TextLessonNextProps {
  onPractice: () => void;
}

/**
 * The step after reading. The lesson used to end on its last paragraph with
 * nothing below it; the product's loop is learn, then practise, so the end of
 * the text hands the reader to the Practice tab.
 *
 * No fill of its own: it sits inside the lesson panel, which is already
 * `bg-card/70`, so a solid `bg-card` read as a white slab on it in light mode.
 * The hairline alone separates it, the same way source rows sit in their panel.
 */
export function TextLessonNext({ onPractice }: TextLessonNextProps) {
  return (
    <div className='mt-12 flex flex-col gap-4 rounded-xl border border-foreground/10 p-5 sm:flex-row sm:items-center'>
      <div
        className='flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-practice/10 text-practice'
        aria-hidden
      >
        <Target className='h-5 w-5' />
      </div>
      <div className='min-w-0 flex-1'>
        <p className='font-semibold text-foreground'>
          Ready to check what stuck?
        </p>
        <p className='mt-0.5 text-sm text-muted-foreground'>
          Quiz yourself on this lesson while it is still fresh.
        </p>
      </div>
      <Button onClick={onPractice} className='h-11 shrink-0 sm:h-9'>
        Practice this lesson
        <ArrowRight />
      </Button>
    </div>
  );
}
